import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import styled from 'styled-components';
import CompanyTitleField from 'talent_matching/components/shared/company_title_field';
import Bookmark from 'talent_matching/components/shared/bookmark';
import { type Person, Row as PersonRow } from 'talent_matching/types';
import { FraudIcon } from 'fraud/fraud_icon';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import BaseCandidateCell from 'talent_matching/components/shared/base_candidate_cell';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching');

const MetadataText = styled.span`
  color: ${FullPalette.GREY_900};
`;

const PrimaryText = styled.span`
  color: ${FullPalette.EVERGREEN_700};
`;

type Props = {
  candidate: Person;
  isRowSelected: boolean;
  isInSidepanel: boolean;
  onRejectSuccess: (row: PersonRow, closeModal: () => void) => void;
};

const CandidateOverviewCell = ({
  candidate,
  isRowSelected,
  isInSidepanel,
  onRejectSuccess,
}: Props) => {
  const { canUseLocations } = useUserContext();

  const {
    title,
    company,
    latestEmployment,
    isBookmarked,
    fraudReportHighRisk,
    resume,
  } = candidate;

  const resumeAddress = resume?.details?.address;
  const {
    canViewFraudReports,
    paths,
    canAdvance,
    canReject,
    canDownloadCandidateData,
  } = useTalentMatchingTabContext();

  const showFraudIcon = canViewFraudReports && fraudReportHighRisk;
  const resumeEmployment = resume?.details?.employments?.[0];
  const showCompanyTitle =
    title || company || latestEmployment || resumeEmployment;
  const showBookmark = !!isBookmarked || isRowSelected || isInSidepanel;

  return (
    <BaseCandidateCell
      candidate={candidate}
      onRejectSuccess={onRejectSuccess}
      paths={paths}
      canAdvance={canAdvance}
      canReject={canReject}
      canDownloadCandidateData={canDownloadCandidateData}
      nameAdornment={
        <>
          {showFraudIcon && <FraudIcon size="sm" />}
          <Stack sx={{ marginTop: -0.5 }}>
            <Bookmark
              className={showBookmark ? '' : 'hide'}
              application={candidate}
              size="seedling-only-sm"
            />
          </Stack>
        </>
      }
    >
      {showCompanyTitle && (
        <CompanyTitleField
          title={title}
          company={company}
          latestEmployment={latestEmployment}
          resumeEmployment={resumeEmployment}
        />
      )}
      {canUseLocations && resumeAddress && (
        <SecondaryBody>
          <MetadataText>{t('table.labels.address_label')}</MetadataText>
          <PrimaryText className="pii-blur">{resumeAddress}</PrimaryText>
        </SecondaryBody>
      )}
    </BaseCandidateCell>
  );
};

export default CandidateOverviewCell;
